import { UIBrick, registerBrick, BrickContext } from 'olympe';
import ReactDOM from 'react-dom'
import React, { useState } from 'react';
import { GetLookupDataSku } from './SKUService'
import PropTypes from 'prop-types'
import { FiltersAutocomplete } from '../shared/FiltersAutocomplete.jsx';
import { combineProps, createEvents, updateSelectedArray } from '../shared/_helpers';

export default class SKUMUIAutocomplete extends UIBrick {

    /**
     * This method runs when the brick is ready in the HTML DOM.
     * @override
     * @param {!BrickContext} context
     * @param {!Element} elementDom
     */
    draw(context, elementDom) {

        const events = createEvents([
            {
                name: 'onChange',
                event: context.getEvent('On Change'),
                before: (event, values) => {
                    context.getProperty('MUI Value').set(JSON.stringify(values))
                },
            },
            {
                name: 'onUpdate',
                event: context.getEvent('On Change'),
                before: (event, values) => {
                    context.getProperty('MUI Options').set(JSON.stringify(values))
                },
            },
        ])

        combineProps([
            // TODO remove all the props that are not used here anymore from DRAW
            // Add limitcheck property to set a input text value 
            { name: 'disabled', prop: context.getProperty('MUI Disabled'), defaultValue: false },
            { name: 'id', prop: context.getProperty('MUI ID'), defaultValue: 'mui_autocomplete' },
            { name: 'values', prop: context.getProperty('MUI Value'), defaultValue: '[]' },
            { name: 'options', prop: context.getProperty('MUI Options'), defaultValue: '[]' },
            { name: 'type', prop: context.getProperty('Type'), defaultValue: '' },
            { name: 'plantsSelected', prop: context.getProperty('Plants Selected'), defaultValue: '[]' },
            { name: 'producingCompanySelected', prop: context.getProperty('Producing Company Selected'), defaultValue: '[]' },
            { name: 'sellingCompanySelected', prop: context.getProperty('Selling Company Selected'), defaultValue: '[]' },
            { name: 'businessSelected', prop: context.getProperty('Business Selected'), defaultValue: '[]' },
            { name: 'nestecProductCategorySelected', prop: context.getProperty('Nestec Product Category Selected'), defaultValue: '[]' },
            { name: 'productSelected', prop: context.getProperty('Product Selected'), defaultValue: '[]' },
            { name: 'marketCriticalSelected', prop: context.getProperty('Market Critical Selected'), defaultValue: '[]' },
            { name: 'marketShortlistSelected', prop: context.getProperty('Market Shortlist Selected'), defaultValue: '[]' },
            { name: 'exportedProductSelected', prop: context.getProperty('Exported Product Selected'), defaultValue: '[]' },
            { name: 'modifiedBySelected', prop: context.getProperty('Modified By Selected'), defaultValue: '[]' },
            { name: 'nnsStatusSelected', prop: context.getProperty('NNS Status Selected'), defaultValue: '[]' },
            { name: 'pl3CategorySelected', prop: context.getProperty('PL3 Category Selected'), defaultValue: '[]' },
            { name: 'pl2BusinessSelected', prop: context.getProperty('PL2 Business Selected'), defaultValue: '[]' },
            { name: 'rangeBrandSelected', prop: context.getProperty('Range Brand Selected'), defaultValue: '[]' },
            { name: 'limitCheck', prop: context.getProperty('Limit Check') },
        ]).subscribe(props => {
            ReactDOM.render(<SKUMUIAutocomplete.Component {...props} {...events} context={context} />, elementDom)
        })
    }
}

/**
 *
 * @param {Object} props
 * @param {BrickContext} props.context
 * @param {string} props.options
 * @param {string} props.type
 * @param {string} props.plantsSelected
 * @param {string} props.producingCompanySelected
 * @param {string} props.sellingCompanySelected
 * @param {string} props.businessSelected
 * @param {string} props.nestecProductCategorySelected
 * @param {string} props.productSelected
 * @param {string} props.marketCriticalSelected
 * @param {string} props.marketShortlistSelected
 * @param {string} props.exportedProductSelected
 * @param {string} props.modifiedBySelected
 * @param {string} props.nnsStatusSelected
 * @param {string} props.pl3CategorySelected
 * @param {string} props.pl2BusinessSelected
 * @param {string} props.rangeBrandSelected
 * @param {boolean} props.disabled
 * @param {string} props.id
 * @param {function(Event, string)} props.onChange
 * @param {function(Event, string)} props.onUpdate
 * @return {JSX.Element[]}
 * @constructor
 */

SKUMUIAutocomplete.Component = props => {
    const updateHeight = () => {
        let element = {};
        if (props?.id) {            
            element = document.getElementById(props.id);
        }        
        if (element && element?.parentElement) {
            props.context.set('MUI Height', Math.max(element?.parentElement?.clientHeight, 70));
        } else {
            props.context.set('MUI Height', 70);
        }
    };

    // in case of Autocomplete plants, process the options coming directly from the local storage
    let optionsArray = [];
    if (props.type === 'plant') {
        try {
            optionsArray = JSON.parse(props.options);
            optionsArray = optionsArray['roles']['roles-details']['company-codes']['plants'];
        } catch (e) {/* empty */ }
    }

    // Array for the filters
    // TODO all these props could be replaced by a single JSON object shared between the filters (probably easier after upgrade to DRAW 2.0)
    let filtersArrays = {
        plantsSelectedArray: [],
        producingCompanySelectedArray: [],
        sellingCompanySelectedArray: [],
        businessSelectedArray: [],
        nestecProductCategorySelectedArray: [],
        productSelectedArray: [],
        marketCriticalSelectedArray: [],
        marketShortlistSelectedArray: [],
        exportedProductSelectedArray: [],
        modifiedBySelectedArray: [],
        nnsStatusSelectedArray: [],
        pl3CategorySelectedArray: [],
        pl2BusinessSelectedArray: [],
        rangeBrandSelectedArray: [],
    }

    filtersArrays.plantsSelectedArray = updateSelectedArray(props.plantsSelected, 'plant')
    filtersArrays.producingCompanySelectedArray = updateSelectedArray(props.producingCompanySelected, 'producingCompanyCode')
    filtersArrays.sellingCompanySelectedArray = updateSelectedArray(props.sellingCompanySelected, 'sellingCompanyCode')
    filtersArrays.businessSelectedArray = updateSelectedArray(props.businessSelected, 'business')
    filtersArrays.nestecProductCategorySelectedArray = updateSelectedArray(props.nestecProductCategorySelected, 'nestecProductCategory')
    filtersArrays.productSelectedArray = updateSelectedArray(props.productSelected, 'product')
    filtersArrays.marketCriticalSelectedArray = updateSelectedArray(props.marketCriticalSelected, 'marketCritical')
    filtersArrays.marketShortlistSelectedArray = updateSelectedArray(props.marketShortlistSelected, 'marketShortlist')
    filtersArrays.exportedProductSelectedArray = updateSelectedArray(props.exportedProductSelected, 'exportedProduct')
    filtersArrays.modifiedBySelectedArray = updateSelectedArray(props.modifiedBySelected, 'modifiedBy')
    filtersArrays.nnsStatusSelectedArray = updateSelectedArray(props.nnsStatusSelected, 'nnsStatus')
    filtersArrays.pl3CategorySelectedArray = updateSelectedArray(props.pl3CategorySelected, 'pl3Category')
    filtersArrays.pl2BusinessSelectedArray = updateSelectedArray(props.pl2BusinessSelected, 'pl2Business')
    filtersArrays.rangeBrandSelectedArray = updateSelectedArray(props.rangeBrandSelected, 'rangeBrand')

    const fetchOptions = async (searchValue) => {
        if (props.type !== 'plant') {
            const response = await GetLookupDataSku(
                searchValue,
                props.type,
                filtersArrays,
            );
            return response?.slice(0, 100000);
        } else {
            return optionsArray;
        }
        // not needed to export available options, as it seems to be never used in DRAW
        //props.onUpdate(null, flattenArray.slice(0, 100000))
    };

    let getOptionLabel;
    if (props.type !== 'plant') {
        getOptionLabel = (item) => {
            if (typeof item.id === 'string' && item.id !== '' && item.description !== '') {
                return parseInt(item.id) ?
                    `${parseInt(item.id)} - ${item.description}` :
                    `${item.id} - ${item.description}`;
            } else if (typeof item.id === 'string' && item.id !== '') {
                return `${item.id}`;
            } else {
                return `${item.description}`;
            }
        };
    } else {
        getOptionLabel = (item) => {
            if (item.market !== undefined) {
                return item.market + ' - ' + item.plantID + ' - ' + item.plantDescription;
            } else {
                return item.description; // to support 'Select All' option
            }
        };
    }

    // TODO could export array of object instead of strings
    //     right now I need to store into an intermediate state
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [showSelectAll, setShowSelectAll] = useState(false);

    const onChangeHandler = (newSelection, selectedAll) => {
        const exportedValues = newSelection
            .filter((opt) => opt.notValid !== true)
            .map((opt) => getOptionLabel(opt));
        props.onChange(
            null,
            exportedValues,
        );
        setSelectedOptions(newSelection);
        setShowSelectAll(selectedAll);
    };

    // support clearing when clicking on the external 'clear' button
    if (props.values === '[]' && selectedOptions.length > 0) {
        setSelectedOptions([]);
        setShowSelectAll(false);
    }

    return <FiltersAutocomplete
        id={props.id}
        disabled={props.disabled}
        fetchOptions={fetchOptions}
        getOptionLabel={getOptionLabel}
        selectedOptions={selectedOptions}
        showSelectAll={showSelectAll}
        onChange={onChangeHandler}
        updateHeight={updateHeight}
        limitCheck={props.limitCheck}
    />;
}

SKUMUIAutocomplete.Component.propTypes = {
    context: PropTypes.object,
    options: PropTypes.string,
    type: PropTypes.string,
    plantsSelected: PropTypes.string,
    producingCompanySelected: PropTypes.string,
    sellingCompanySelected: PropTypes.string,
    businessSelected: PropTypes.string,
    nestecProductCategorySelected: PropTypes.string,
    productSelected: PropTypes.string,
    marketCriticalSelected: PropTypes.string,
    marketShortlistSelected: PropTypes.string,
    exportedProductSelected: PropTypes.string,
    modifiedBySelected: PropTypes.string,
    nnsStatusSelected: PropTypes.string,
    pl3CategorySelected: PropTypes.string,
    pl2BusinessSelected: PropTypes.string,
    rangeBrandSelected: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    limitCheck: PropTypes.number,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
}

registerBrick('017e677ea4eb956400ce', SKUMUIAutocomplete);
